import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface InputType {
  Id: number;
  Name: string;
  Active: boolean;
}

export interface Input {
  Id: number;
  Name: string;
  InputTypeId: number;
  InputType?: InputType;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface CreateInputRequest {
  Name: string;
  InputTypeId: number;
  Active: boolean;
}

export interface UpdateInputRequest {
  Id: number;
  Name: string;
  InputTypeId: number;
  Active: boolean;
}

export interface InputListResponse {
  data: Input[];
  total: number;
}

export interface InputResponse {
  data: Input;
}

function ensureArray<T>(value: any): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && Array.isArray(value.data)) return value.data as T[];
  return [] as T[];
}

// API Service
export const inputsApi = {
  // CRUD Operations
  getAll: (): Promise<Input[]> =>
    api.get('/inputs'),

  getById: (id: number): Promise<Input> =>
    api.get(`/inputs/${id}`),

  create: (input: CreateInputRequest): Promise<Input> =>
    api.post('/inputs', input),

  update: (input: UpdateInputRequest): Promise<Input> =>
    api.put(`/inputs/${input.Id}`, input),

  delete: (id: number): Promise<void> =>
    api.delete(`/inputs/${id}`),

  // Specific Operations
  getInputTypes: (): Promise<InputType[]> =>
    api.get('/inputtype'),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<void> =>
    api.post('/inputs/bulk/activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<void> =>
    api.post('/inputs/bulk/deactivate', { ids }),

  // Statistics
  getStatistics: async (): Promise<{ total: number; active: number; inactive: number }> => {
    try {
      return await api.get('/inputs/statistics');
    } catch (err) {
      // Fallback: derive from full list
      const list = await api.get<any>('/inputs');
      const items = ensureArray<Input>(list);
      const total = items.length;
      const active = items.filter(i => i.Active).length;
      const inactive = total - active;
      return { total, active, inactive };
    }
  },

  // Validation helpers
  validation: {
    validateInput: (input: Partial<CreateInputRequest> | Partial<UpdateInputRequest> | Partial<Input>): string[] => {
      const errors: string[] = [];

      if (!input.Name || input.Name.trim().length === 0) {
        errors.push('Name is required');
      }

      if (input.Name && input.Name.trim().length > 100) {
        errors.push('Name must be 100 characters or less');
      }

      if (!input.InputTypeId || input.InputTypeId <= 0) {
        errors.push('Input Type is required');
      }

      return errors;
    },

    validateBeforeSave: (input: CreateInputRequest | UpdateInputRequest): string[] => {
      const errors = inputsApi.validation.validateInput(input);

      // Additional validation for save operations
      if (input.InputTypeId && typeof input.InputTypeId !== 'number') {
        errors.push('Please select a valid input type');
      }

      return errors;
    }
  }
};

export default inputsApi; 